import { Button, Col, Form, Input, message, Row, Typography, Upload, Tabs, Tag} from 'antd';
import axios from 'axios';
import { setCookie } from 'cookies-next';
import React, { useState } from 'react';
import { GrLinkNext } from "react-icons/gr";
import { IoCloudUploadOutline } from "react-icons/io5";
import { v4 as uuidv4 } from "uuid";
import { makeApiRequest } from '../../../../shared/api';
import { RobotOutlined, LinkOutlined, FileTextOutlined, GlobalOutlined, EditOutlined, InfoCircleOutlined, BulbOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const StartHere = ({ setCurrent, setKbName, setKbDescription, kbName, kbDescription }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('file');
  const [urlInput, setUrlInput] = useState('');
  const [textContent, setTextContent] = useState('');

  // Function to get the upload URL and fields from backend
  const getUploadUrl = async() => {
    try {
      const newUuid = uuidv4(); // Generate a new UUID
      const fileTypeMap = {
      'application/pdf': 'pdf',
      'text/html': 'html',
      'text/plain': 'txt'
    };
      let params = {
        file_id: newUuid.slice(0, 8), // unique id
        file_type: activeTab === 'file' ? fileTypeMap[fileList[0].type] || fileList[0].type.split("/")[1] : 'url',
      };

      setCookie('fb_file_id', params.file_id);

      const response = await makeApiRequest("POST","kb/requestUploadUrl", params);

      if (!response) {
        console.error("Invalid response from makeApiRequest:", response);
        message.error(
          "Failed to get upload URL: Invalid response from the server."
        );
        return null;
      }

      return response;

    } catch (error) {
      console.error("Error getting upload URL:", error);
      message.error("Failed to get upload URL. Please try again.");
      return null;
    }
  };

  const handleUpload = async() => {
    // Check if required fields are filled based on the active tab
    if (activeTab === "file") {
      if (!fileList || fileList.length === 0) {
        message.error("Please upload a file.");
        return;
      }
    } else if (activeTab === "url") {
      if (!urlInput || urlInput.trim() === "") {
        message.error("Please enter a valid URL.");
        return;
      }
    } else if (activeTab === "text") {
      if (!textContent || !textContent.trim()) {
        message.error("Please enter some content");
        return;
      }

      // Store the text content and a marker for text input
      setCookie("fb_text_content", textContent);
      setCookie("fb_file_url", "text_input");

      message.success("Content processed successfully!");
      setCurrent(1);
    }

    if (!kbName || kbName.trim() === '') {
      message.error('Please enter a name.');
      return;
    }

    if (uploading) {
      return;
    }

    setUploading(true);

    try {
      if (activeTab === 'file') {
      const uploadData = await getUploadUrl(); // Get the upload data
      if (!uploadData) {
        setUploading(false);
        return;
      }

      const formData = new FormData();

      // Append the fields to the form data
      if (uploadData.fields) {
        for (const key in uploadData.fields) {
          formData.append(key, uploadData.fields[key]);
        }
      }

      // Append the file to the form data
      formData.append('file', fileList[0]);  // Access file obj

      // Store the complete file URL as a cookie
      const completeFileUrl = `${uploadData.url}${uploadData.fields.key}`;
      setCookie('fb_file_url', completeFileUrl);

      await axios.post(uploadData.url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      message.success('File uploaded successfully!');
     } else if (activeTab === 'url') {
        if (!urlInput || !urlInput.trim()) {
          message.error("Please enter a valid URL");
          return;
        }
        try {
          new URL(urlInput);
        } catch (_) {
          message.error("Please enter a valid URL format");
          return;
        }

        // Store URL in cookie for knowledge analysis
        setCookie("fb_file_url", urlInput);
        message.success("URL processed successfully!");
        setCurrent(1);
      } else if (activeTab === 'text') {
        if (!textContent.trim()) {
          message.error("Please enter some content");
          return;
        }

        // Store the text content and a marker for text input
        setCookie("fb_text_content", textContent);
        setCookie("fb_file_url", "text_input");
      }

      setUploading(false);

      // Redirect to the next step
      setCurrent(1);

    } catch (error) {
      setUploading(false);
      message.error('File upload failed: ' + error.message);
    }
  };

  const props = {
    multiple: false,
    maxCount: 1,
    accept: ".pdf, .html, .txt",
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const maxSize = 1 * 1024 * 1024;
      const allowedTypes = {
      'application/pdf': 'pdf',
      'text/html': 'html',
      'text/plain': 'txt'
    };

      if (!Object.keys(allowedTypes).includes(file.type)) {
        message.error("Invalid file type. Only PDF, HTML, and TXT files are allowed.");
        return false;
      }

      if (file.size > maxSize) {
        message.error("File size must be 1MB or less. Please try again!");
        return false;
      }

      const fileExtension = allowedTypes[file.type];
      setCookie('fb_file_extension', fileExtension);

      setFileList([file]);
      return false;
    },
    fileList,
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  return (
    <div style={{ padding: "40px 20px", textAlign: "center" }}>
      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12} xl={14}>
          <div style={{ textAlign: "center", marginBottom: "15px" }}>
            <div style={{ marginBottom: "16px" }}>
              <RobotOutlined style={{ fontSize: "48px", color: "#1890ff" }} />
            </div>
            <Title
              level={3}
              style={{
                fontWeight: "600",
                marginBottom: "8px",
                background: "linear-gradient(90deg, #1890ff 0%, #6739b6 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Get started with building your FaceBot
            </Title>
          </div>
          <Title level={5} style={{ marginTop: 8 }}>
            Bring together all the insights about your product / service
          </Title>
          <Text
            style={{
              display: "block",
              marginTop: 14,
              padding: "0 24px",
              color: "gray",
            }}
          >
            Gathering and consolidating all the knowledge you have about your
            products or services is essential for creating a comprehensive
            understanding of what you offer. By organizing this information into
            accessible formats, such as a URL, PDF, or Text documents, you
            ensure that it's readily available for analysis. Please select your
            preferred method below and proceed.
          </Text>
        </Col>
      </Row>

      <Row justify="center" style={{ marginTop: 20 }}>
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <Form
            layout="vertical"
            style={{ maxWidth: "600px", margin: "0 auto" }}
          >
            <Form.Item
              label="Name"
              required
              rules={[{ required: true, message: "Please enter a name!" }]}
            >
              <Input
                placeholder="Enter name"
                value={kbName}
                size="large"
                onChange={(e) => setKbName(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              label="Description"
              rules={[
                { required: true, message: "Please enter a description!" },
              ]}
            >
              <Input.TextArea
                rows={2}
                placeholder="Enter description"
                value={kbDescription}
                size="large"
                onChange={(e) => setKbDescription(e.target.value)}
              />
            </Form.Item>

            <div
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                padding: "20px",
              }}
            >
              <Tabs
                defaultActiveKey="file"
                onChange={handleTabChange}
                centered
                style={{
                  backgroundColor: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <TabPane
                  tab={
                    <span>
                      <FileTextOutlined /> Upload File
                    </span>
                  }
                  key="file"
                >
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    <Upload.Dragger {...props}>
                      <div
                        className="ant-upload-drag-icon"
                        style={{ marginBottom: "16px" }}
                      >
                        <IoCloudUploadOutline
                          size={48}
                          style={{ color: "#1890ff" }}
                        />
                      </div>
                      <p
                        className="ant-upload-text"
                        style={{ fontSize: "16px", margin: "16px 0" }}
                      >
                        Drag & Drop files here or{" "}
                        <span
                          style={{
                            color: "#1890ff",
                            cursor: "pointer",
                            fontWeight: 500,
                          }}
                        >
                          Click to Browse
                        </span>
                      </p>
                      <div
                        style={{
                          backgroundColor: "#f6f6f6",
                          padding: "12px",
                          borderRadius: "8px",
                          margin: "16px auto",
                          maxWidth: "80%",
                        }}
                      >
                        <p className="ant-upload-hint" style={{ margin: 0 }}>
                          <div style={{ marginBottom: "8px" }}>
                            Supported formats: <Tag color="blue">PDF</Tag>{" "}
                            <Tag color="blue">HTML</Tag>{" "}
                            <Tag color="blue">TXT</Tag>
                          </div>
                          <div>
                            Max file size: <Tag color="green">1MB</Tag>
                          </div>
                        </p>
                      </div>
                    </Upload.Dragger>
                  </div>
                </TabPane>

                <TabPane
                  tab={
                    <span>
                      <GlobalOutlined /> Website URL
                    </span>
                  }
                  key="url"
                >
                  <div style={{ padding: "30px 20px", textAlign: "center" }}>
                    <div style={{ marginBottom: "24px" }}>
                      <LinkOutlined
                        style={{ fontSize: "40px", color: "#1890ff" }}
                      />
                    </div>
                    <Title level={5} style={{ marginBottom: "16px" }}>
                      Enter Website URL
                    </Title>
                    <Text
                      type="secondary"
                      style={{ display: "block", marginBottom: "24px" }}
                    >
                      Provide the URL of your webpage containing product or
                      service information
                    </Text>
                    <Input
                      prefix={<GlobalOutlined style={{ color: "#1890ff" }} />}
                      placeholder="https://example.com/your-product-page"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      size="large"
                      style={{
                        maxWidth: "80%",
                        marginBottom: "16px",
                      }}
                    />
                    <div
                      style={{
                        backgroundColor: "#f6f6f6",
                        padding: "12px",
                        borderRadius: "8px",
                        maxWidth: "80%",
                        margin: "0 auto",
                      }}
                    >
                      <Text type="secondary">
                        <InfoCircleOutlined style={{ marginRight: "8px" }} />
                        Make sure the URL is publicly accessible
                      </Text>
                    </div>
                  </div>
                </TabPane>

                <TabPane
                  tab={
                    <span>
                      <EditOutlined /> Direct Text
                    </span>
                  }
                  key="text"
                >
                  <div style={{ padding: "30px 20px", textAlign: "center" }}>
                    <Title level={5} style={{ marginBottom: "16px" }}>
                      Enter Content Directly
                    </Title>
                    <Text
                      type="secondary"
                      style={{ display: "block", marginBottom: "24px" }}
                    >
                      Write or paste your product/service information here
                    </Text>
                    <Input.TextArea
                      rows={8}
                      placeholder="Enter detailed information about your product or service here..."
                      size="large"
                      value={textContent}
                      onChange={(e) => setTextContent(e.target.value)}
                      style={{
                        marginBottom: "16px",
                        resize: "vertical",
                        minHeight: "200px",
                      }}
                    />
                    <div
                      style={{
                        backgroundColor: "#f6f6f6",
                        padding: "12px",
                        borderRadius: "8px",
                        maxWidth: "80%",
                        margin: "0 auto",
                      }}
                    >
                      <Text type="secondary">
                        <BulbOutlined
                          style={{ marginRight: "8px", color: "#faad14" }}
                        />
                        Tip: Include key features, benefits, and frequently
                        asked questions
                      </Text>
                    </div>
                  </div>
                </TabPane>
              </Tabs>
            </div>
          </Form>
          <Form.Item style={{ textAlign: "center", marginTop: 20 }}>
            <Button
              type="primary"
              size="large"
              icon={<GrLinkNext />}
              iconPosition="end"
              style={{ width: "10rem" }}
              onClick={handleUpload}
              disabled={
                (activeTab === "file" &&
                  (!fileList || fileList.length === 0)) ||
                (activeTab === "url" &&
                  (!urlInput || urlInput.trim() === "")) ||
                uploading ||
                !kbName ||
                kbName.trim() === ""
              }
              loading={uploading}
            >
              Continue
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default StartHere;